// Node thumbnails, remembered in this browser so a folder seen before shows
// its pictures at once: after a reload, in a new tab, on the next visit.
//
// Two layers, both keyed by thumbnailKey (organization / thumbnail id / the
// moment it was last uploaded, so a new upload is a new key and never shows
// the old picture):
//   - memory: this page's blobs, least recently used dropped past a cap;
//   - the browser's Cache Storage (CACHE_NAME): the same blobs across reloads
//     and tabs. Its index in localStorage (INDEX_KEY: key -> size and last
//     use) drives least-recently-used eviction past maxEntries / maxBytes.
//
// Every storage call is guarded: when Cache Storage or localStorage is
// missing or refuses (an insecure page, private browsing, blocked site data,
// a full disk), the cache answers "not here" and the card loads from the
// network as before. A read that hasn't answered within lookupTimeout counts
// as a miss, and that page stops asking the browser's storage.
//
// Signing out empties it (forget, from storage/auth.js removeTokens): a
// shared computer must not keep one person's organization's pictures. The
// flag (CLEAR_FLAG_KEY) is set synchronously first, so a sign-out that leaves
// the page before the asynchronous delete finishes is completed by the next
// page before it reads anything.

export const CACHE_NAME = 'edxr-thumbnails-v1'
export const INDEX_KEY = 'edxr-thumbnails-v1-index'
export const CLEAR_FLAG_KEY = 'edxr-thumbnails-clear'

const MB = 1024 * 1024

export const LIMITS = {
  maxEntries: 500,
  maxBytes: 50 * MB,
  // A few thumbnails are many megabytes; one of those is kept for the page
  // but not stored, so it can't push dozens of others out.
  maxEntryBytes: 5 * MB,
  memoryEntries: 200,
  memoryBytes: 40 * MB
}

const PREFIX = '/__thumbnail-cache__/'

// The organization, the thumbnail and when it was last uploaded (content's
// asset-thumbnail `updatedAt`, which storage moves on every upload). Null when
// any is missing: such a thumbnail is fetched, never stored.
export const thumbnailKey = (organizationID, asset) => {
  if (!organizationID || !asset || !asset.id || !asset.updatedAt) return null
  return `${organizationID}/${asset.id}/${asset.updatedAt}`
}

const assetIDOf = key => String(key).split('/')[1]

export const createThumbnailCache = ({
  getCacheStorage = () => window.caches,
  getStorage = () => window.localStorage,
  makeResponse = blob => new Response(blob, { headers: { 'Content-Type': blob.type || 'application/octet-stream' } }),
  now = () => Date.now(),
  origin = () => window.location.origin,
  flushDelay = 250,
  lookupTimeout = 1500,
  limits = LIMITS
} = {}) => {
  const memory = new Map() // key -> Blob, oldest use first
  let memoryBytes = 0
  let pending = { added: new Map(), touched: new Map(), removed: new Set() }
  let timer = null
  let reconciled = false
  // Set when the browser's storage didn't answer a read in time: this page
  // stops asking it and goes to the network, as before.
  let stalled = false

  const requestURL = key => `${origin()}${PREFIX}${key.split('/').map(encodeURIComponent).join('/')}`
  const keyOf = url => {
    try {
      const { pathname } = new URL(url)
      if (!pathname.startsWith(PREFIX)) return null
      return pathname.slice(PREFIX.length).split('/').map(decodeURIComponent).join('/')
    } catch (e) {
      return null
    }
  }

  const storage = () => {
    try {
      return getStorage() || null
    } catch (e) {
      return null
    }
  }
  const storageGet = name => {
    try {
      const s = storage()
      return s ? s.getItem(name) : null
    } catch (e) {
      return null
    }
  }
  const storageSet = (name, value) => {
    try {
      const s = storage()
      if (s) s.setItem(name, value)
    } catch (e) {}
  }
  const storageRemove = name => {
    try {
      const s = storage()
      if (s) s.removeItem(name)
    } catch (e) {}
  }

  const readIndex = () => {
    try {
      const index = JSON.parse(storageGet(INDEX_KEY))
      return index && typeof index === 'object' ? index : {}
    } catch (e) {
      return {}
    }
  }

  // The browser's cache, after finishing a sign-out's clear that was cut
  // short. Throws when it can't be had; callers treat that as a miss.
  const openCache = async () => {
    if (stalled) return null
    const cacheStorage = getCacheStorage()
    if (!cacheStorage) return null
    if (storageGet(CLEAR_FLAG_KEY)) {
      await cacheStorage.delete(CACHE_NAME)
      storageRemove(INDEX_KEY)
      storageRemove(CLEAR_FLAG_KEY)
    }
    return cacheStorage.open(CACHE_NAME)
  }

  const forgetMemory = key => {
    if (!memory.has(key)) return
    memoryBytes -= memory.get(key).size
    memory.delete(key)
  }

  const remember = (key, blob) => {
    forgetMemory(key)
    memory.set(key, blob)
    memoryBytes += blob.size
    for (const [oldest] of memory) {
      if (memory.size <= limits.memoryEntries && memoryBytes <= limits.memoryBytes) break
      if (oldest === key) break
      forgetMemory(oldest)
    }
  }

  const scheduleFlush = () => {
    if (timer) return
    timer = setTimeout(() => {
      timer = null
      flush()
    }, flushDelay)
  }

  const unindex = key => {
    pending.added.delete(key)
    pending.touched.delete(key)
    pending.removed.add(key)
    scheduleFlush()
  }

  // A use, for least-recently-used eviction (written with the next flush).
  const touch = key => {
    if (!key) return
    pending.touched.set(key, now())
    scheduleFlush()
  }

  // This page's copy, at once (no storage, no network).
  const peek = key => {
    if (!key || !memory.has(key)) return undefined
    const blob = memory.get(key)
    memory.delete(key)
    memory.set(key, blob)
    return blob
  }

  const get = async key => {
    if (!key) return undefined
    const hit = peek(key)
    if (hit) {
      touch(key)
      return hit
    }
    try {
      const blob = await withinTimeout(read(key))
      if (!blob) return undefined
      remember(key, blob)
      touch(key)
      return blob
    } catch (e) {
      return undefined
    }
  }

  const read = async key => {
    const cache = await openCache()
    if (!cache) return undefined
    const url = requestURL(key)
    const response = await cache.match(url)
    const blob = response ? await response.blob() : null
    if (!blob || !blob.size) {
      if (response) await cache.delete(url)
      unindex(key)
      return undefined
    }
    return blob
  }

  const withinTimeout = promise =>
    new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        stalled = true
        resolve(undefined)
      }, lookupTimeout)
      promise.then(
        value => {
          clearTimeout(timeout)
          resolve(value)
        },
        error => {
          clearTimeout(timeout)
          reject(error)
        }
      )
    })

  const put = async (key, blob) => {
    if (!key || !blob || !blob.size) return
    remember(key, blob)
    if (blob.size > limits.maxEntryBytes) return
    try {
      const cache = await openCache()
      if (!cache) return
      await cache.put(requestURL(key), makeResponse(blob))
      pending.removed.delete(key)
      pending.added.set(key, { s: blob.size, t: now() })
      scheduleFlush()
    } catch (e) {}
  }

  // Writes the pending uses, additions and removals to the index, then
  // removes the least recently used past the caps. The first flush of a page
  // also removes stored entries the index has lost track of (a write lost to
  // another tab), so storage can't grow past the caps unseen.
  const flush = async () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    const { added, touched, removed } = pending
    pending = { added: new Map(), touched: new Map(), removed: new Set() }

    const index = readIndex()
    added.forEach((entry, key) => {
      index[key] = entry
    })
    touched.forEach((t, key) => {
      if (index[key]) index[key].t = Math.max(index[key].t || 0, t)
    })
    removed.forEach(key => {
      delete index[key]
    })

    const byAge = Object.keys(index).sort((a, b) => index[a].t - index[b].t)
    let count = byAge.length
    let bytes = byAge.reduce((sum, key) => sum + (index[key].s || 0), 0)
    const evicted = []
    for (const key of byAge) {
      if (count <= limits.maxEntries && bytes <= limits.maxBytes) break
      evicted.push(key)
      count -= 1
      bytes -= index[key].s || 0
      delete index[key]
    }
    storageSet(INDEX_KEY, JSON.stringify(index))

    try {
      const cache = await openCache()
      if (!cache) return
      await Promise.all(evicted.map(key => cache.delete(requestURL(key))))
      if (!reconciled) {
        reconciled = true
        const live = readIndex()
        const requests = await cache.keys()
        const orphans = requests.filter(request => {
          const key = keyOf(request.url)
          return !key || (!live[key] && !pending.added.has(key))
        })
        await Promise.all(orphans.map(request => cache.delete(request)))
      }
    } catch (e) {}
  }

  // Every stored version of one thumbnail (uploaded again, or deleted).
  const invalidate = async (organizationID, assetID) => {
    const id = String(assetID)
    const matches = key => assetIDOf(key) === id && (!organizationID || key.split('/')[0] === String(organizationID))
    ;[...memory.keys()].filter(matches).forEach(forgetMemory)
    Object.keys(readIndex()).filter(matches).forEach(unindex)
    ;[...pending.added.keys()].filter(matches).forEach(unindex)
    try {
      const cache = await openCache()
      if (!cache) return
      const requests = await cache.keys()
      await Promise.all(requests.filter(request => matches(keyOf(request.url) || '')).map(request => cache.delete(request)))
    } catch (e) {}
  }

  const clear = async () => {
    memory.clear()
    memoryBytes = 0
    pending = { added: new Map(), touched: new Map(), removed: new Set() }
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    storageRemove(INDEX_KEY)
    const cacheStorage = getCacheStorage()
    if (cacheStorage) await cacheStorage.delete(CACHE_NAME)
  }

  // Sign-out. The flag first, synchronously; it is lifted once the browser's
  // copy is really gone.
  const forget = async () => {
    storageSet(CLEAR_FLAG_KEY, '1')
    try {
      await clear()
      storageRemove(CLEAR_FLAG_KEY)
    } catch (e) {}
  }

  return { peek, get, put, touch, flush, invalidate, forget }
}

// The page's one cache.
export const thumbnailCache = createThumbnailCache()

// For storage/auth.js: every way a session ends (sign-out, a refresh that is
// refused, a 401) removes the tokens there.
export const forgetThumbnails = () => thumbnailCache.forget()
